// ---------------------------------------------------------------------------
// ข้อมูลตัวอย่างของทุกโมดูล (mock) — สร้างครั้งเดียวแล้วเก็บใน localStorage
// เปลี่ยน SEED_VERSION เมื่อแก้โครงสร้างข้อมูล เพื่อให้ระบบ seed ใหม่อัตโนมัติ
// ---------------------------------------------------------------------------
import { BUSINESS_MODEL_MAP } from "../config/business-models";
import { getPlan, planModules, priceOf } from "../config/plans";
import { calcVat } from "../utils/thaiTax";

export const SEED_VERSION = "2026-10-13.2";

// --------------------------------------------------------------------------
// utils
// --------------------------------------------------------------------------
let seedState = 987654321;
const rnd = () => {
  seedState = (seedState * 1664525 + 1013904223) % 4294967296;
  return seedState / 4294967296;
};
const pick = (arr) => arr[Math.floor(rnd() * arr.length)];
const int = (min, max) => Math.floor(rnd() * (max - min + 1)) + min;
const chance = (percent) => rnd() * 100 < percent;

const DAY = 86400000;
const now = () => new Date();
const iso = (date) => new Date(date).toISOString();
const shiftDays = (days, hour) => {
  const d = new Date(Date.now() + days * DAY);
  if (hour !== undefined) d.setHours(hour, [0, 15, 30, 45][int(0, 3)], 0, 0);
  return d.toISOString();
};
const dateOnly = (isoString) => String(isoString).slice(0, 10);

const FIRST_TH = ["สมชาย", "วิภา", "ณัฐพล", "ปิยะดา", "อรรถพล", "ชุติมา", "ธนกร", "กมลชนก", "ภาณุพงศ์", "ศิริพร", "จิรายุ", "พิมพ์ชนก", "อนุชา", "เบญจวรรณ", "ณิชา", "กิตติศักดิ์", "รัตนา", "วรพล", "สุชาดา", "ปรมินทร์"];
const LAST_TH = ["ใจดี", "ศรีสุข", "วงศ์วิวัฒน์", "แสงทอง", "บุญมี", "พงษ์ไพบูลย์", "จันทร์เพ็ญ", "อารีย์", "ทองสุข", "เกียรติศักดิ์", "รุ่งเรือง", "ภักดี", "มณีรัตน์", "สายทอง", "วัฒนกุล"];
const NICK = ["ตาล", "บอส", "มิ้น", "เอิร์ธ", "ฟ้า", "กร", "ใหม่", "นุ่น", "ต้น", "แพร", "เจ", "อ้อม"];
const CHANNELS = ["line", "facebook", "instagram", "walkin", "phone", "website"];
const TIERS = ["bronze", "silver", "gold", "platinum"];

const phone = () => `0${pick(["6", "8", "9"])}${String(int(10000000, 99999999)).slice(0, 8)}`;
const personName = () => `${pick(FIRST_TH)} ${pick(LAST_TH)}`;
const emailOf = (index) => `customer${index + 1}@example.com`;
const money = (min, max, step = 50) => Math.round(int(min, max) / step) * step;

// --------------------------------------------------------------------------
// องค์กรตัวอย่าง (multi-tenant)
// --------------------------------------------------------------------------
const ORGS = [
  {
    id: "org_glow",
    name: "Glow Aesthetic Clinic",
    nameEn: "Glow Aesthetic Clinic",
    businessModel: "clinic",
    taxId: "0105566001237",
    vatRegistered: true,
    branchCode: "00000",
    address: "128/9 ถ.สุขุมวิท แขวงคลองเตย เขตคลองเตย กรุงเทพฯ 10110",
    phone: "02-118-9000",
    email: "hello@glowclinic.co.th",
    lineOa: "@glowclinic",
    logoColor: "#6366f1",
    plan: "enterprise_plus",
    planCycle: "yearly",
    planMonths: 12,
  },
  {
    id: "org_ridenow",
    name: "RideNow เช่ามอเตอร์ไซค์",
    nameEn: "RideNow Motorbike Rental",
    businessModel: "vehicle_rental",
    taxId: "0105566005674",
    vatRegistered: true,
    branchCode: "00000",
    address: "45/7 ถ.เจริญประดิษฐ์ ต.รอบเวียง อ.เมือง เชียงราย 57000",
    phone: "053-700-880",
    email: "book@ridenow.co.th",
    lineOa: "@ridenow",
    logoColor: "#0ea5e9",
    plan: "business",
    planCycle: "monthly",
    planMonths: 1,
  },
  {
    id: "org_bloom",
    name: "Bloom Shop ค้าปลีก-ออนไลน์",
    nameEn: "Bloom Shop Retail",
    businessModel: "social_commerce",
    taxId: "0105566009017",
    address: "9/99 ถ.นิมมานเหมินท์ ต.สุเทพ อ.เมือง เชียงใหม่ 50200",
    phone: "052-090-770",
    email: "shop@bloom.co.th",
    lineOa: "@bloomshop",
    logoColor: "#f59e0b",
    plan: "starter",
    planCycle: "monthly",
    planMonths: 1,
  },
];

const BRANCHES = {
  org_glow: [
    { id: "br_glow_1", name: "สาขาสุขุมวิท (สำนักงานใหญ่)", nameEn: "Sukhumvit (HQ)", phone: "02-118-9000", address: "128/9 ถ.สุขุมวิท กรุงเทพฯ", isMain: true, openTime: "10:00", closeTime: "20:00" },
    { id: "br_glow_2", name: "สาขาเซ็นทรัลลาดพร้าว", nameEn: "Central Ladprao", phone: "02-118-9010", address: "1693 ถ.พหลโยธิน กรุงเทพฯ", isMain: false, openTime: "11:00", closeTime: "21:00" },
    { id: "br_glow_3", name: "สาขาพัทยา", nameEn: "Pattaya", phone: "038-410-220", address: "333 ถ.พัทยาสาย 2 ชลบุรี", isMain: false, openTime: "10:00", closeTime: "20:00" },
  ],
  org_ridenow: [
    { id: "br_ride_1", name: "สาขาเมืองเชียงราย", nameEn: "Chiang Rai City", phone: "053-700-880", address: "45/7 ถ.เจริญประดิษฐ์ เชียงราย", isMain: true, openTime: "08:00", closeTime: "19:00" },
    { id: "br_ride_2", name: "สาขาสนามบิน", nameEn: "Airport Branch", phone: "053-700-881", address: "อาคารผู้โดยสาร ท่าอากาศยานเชียงราย", isMain: false, openTime: "07:00", closeTime: "22:00" },
  ],
  org_bloom: [
    { id: "br_bloom_1", name: "หน้าร้านนิมมาน", nameEn: "Nimman Store", phone: "052-090-770", address: "9/99 ถ.นิมมานเหมินท์ เชียงใหม่", isMain: true, openTime: "09:00", closeTime: "20:00" },
    { id: "br_bloom_2", name: "คลังสินค้าสันทราย", nameEn: "Sansai Warehouse", phone: "052-090-771", address: "199 ต.หนองจ๊อม อ.สันทราย เชียงใหม่", isMain: false, openTime: "08:00", closeTime: "17:00" },
  ],
};

const CATALOG = {
  org_glow: [
    { name: "ฉีดฟิลเลอร์ร่องแก้ม 1 cc", type: "service", price: 12900, cost: 6500, duration: 45, unit: "ครั้ง" },
    { name: "โบท็อกซ์ลดกราม 100 ยูนิต", type: "service", price: 9900, cost: 4800, duration: 30, unit: "ครั้ง" },
    { name: "เลเซอร์หน้าใส Pico 5 ครั้ง", type: "package", price: 15900, cost: 6000, duration: 60, unit: "แพ็กเกจ", sessions: 5 },
    { name: "ทรีตเมนต์ผิวหน้า Hydra", type: "service", price: 2500, cost: 900, duration: 60, unit: "ครั้ง" },
    { name: "ร้อยไหมยกกระชับ 20 เส้น", type: "service", price: 24900, cost: 12000, duration: 90, unit: "ครั้ง" },
    { name: "กำจัดขนถาวรรักแร้ 6 ครั้ง", type: "package", price: 8900, cost: 3000, duration: 30, unit: "แพ็กเกจ", sessions: 6 },
    { name: "วิตามินผิวขาว IV Drip", type: "service", price: 3500, cost: 1200, duration: 45, unit: "ครั้ง" },
    { name: "ครีมกันแดด Glow SPF50", type: "product", price: 890, cost: 320, unit: "หลอด" },
    { name: "เซรั่มวิตามินซีเข้มข้น", type: "product", price: 1490, cost: 520, unit: "ขวด" },
    { name: "มาส์กหน้าฟื้นฟูผิว (กล่อง 5 ชิ้น)", type: "product", price: 690, cost: 250, unit: "กล่อง" },
  ],
  org_ridenow: [
    { name: "Honda Click 160i รายวัน", type: "rental", price: 350, cost: 120, unit: "วัน" },
    { name: "Honda PCX 160 รายวัน", type: "rental", price: 450, cost: 150, unit: "วัน" },
    { name: "Yamaha NMAX 155 รายวัน", type: "rental", price: 500, cost: 170, unit: "วัน" },
    { name: "Honda Wave 110i รายวัน", type: "rental", price: 250, cost: 90, unit: "วัน" },
    { name: "แพ็กเกจเช่ารายเดือน Click", type: "rental", price: 5500, cost: 2200, unit: "เดือน" },
    { name: "Toyota Yaris ATIV รายวัน", type: "rental", price: 1290, cost: 600, unit: "วัน" },
    { name: "ประกันเสริมความคุ้มครอง", type: "service", price: 150, cost: 60, unit: "วัน" },
    { name: "หมวกกันน็อกเสริม", type: "product", price: 50, cost: 15, unit: "ใบ" },
    { name: "บริการส่งรถถึงที่พัก", type: "service", price: 200, cost: 80, unit: "ครั้ง" },
    { name: "กล่องเก็บของท้ายรถ", type: "product", price: 100, cost: 40, unit: "วัน" },
  ],
  org_bloom: [
    { name: "เสื้อยืดคอตตอน Oversize", type: "product", price: 390, cost: 150, unit: "ตัว" },
    { name: "กางเกงยีนส์ทรงกระบอก", type: "product", price: 890, cost: 380, unit: "ตัว" },
    { name: "กระเป๋าผ้าแคนวาส", type: "product", price: 290, cost: 95, unit: "ใบ" },
    { name: "เซตเครื่องประดับมินิมอล", type: "product", price: 590, cost: 190, unit: "เซต" },
    { name: "รองเท้าผ้าใบ Daily", type: "product", price: 1290, cost: 560, unit: "คู่" },
    { name: "หมวกบักเก็ตลายทาง", type: "product", price: 350, cost: 120, unit: "ใบ" },
    { name: "เดรสยาวลายดอก", type: "product", price: 790, cost: 300, unit: "ตัว" },
    { name: "เสื้อเชิ้ตโอเวอร์ไซส์", type: "product", price: 690, cost: 260, unit: "ตัว" },
    { name: "ถุงเท้าเซต 3 คู่", type: "product", price: 190, cost: 60, unit: "เซต" },
    { name: "ค่าจัดส่งด่วน", type: "service", price: 60, cost: 45, unit: "ครั้ง" },
  ],
};

const RESOURCES = {
  org_glow: ["ห้องทรีตเมนต์ 1", "ห้องทรีตเมนต์ 2", "ห้องเลเซอร์ A", "ห้องเลเซอร์ B", "ห้องหัตถการ VIP", "ห้องให้คำปรึกษา"],
  org_ridenow: ["ช่องจอด A1", "ช่องจอด A2", "ช่องจอด B1", "จุดส่งมอบสนามบิน"],
  org_bloom: ["โต๊ะแพ็กของ 1", "โต๊ะแพ็กของ 2", "สตูดิโอไลฟ์"],
};

const STAFF_ROLES = {
  org_glow: ["แพทย์", "พยาบาล", "ที่ปรึกษาความงาม", "แคชเชียร์", "ผู้จัดการสาขา"],
  org_ridenow: ["เจ้าหน้าที่หน้าร้าน", "ช่างซ่อมบำรุง", "คนส่งรถ", "ผู้จัดการสาขา"],
  org_bloom: ["แอดมินเพจ", "แพ็กเกอร์", "ไลฟ์สเตอร์", "บัญชี"],
};

const ASSETS = {
  org_glow: [
    { name: "เครื่องเลเซอร์ Pico Plus", code: "LZ-001", category: "เครื่องมือแพทย์", value: 2400000 },
    { name: "เครื่อง Hydra Facial", code: "HF-002", category: "เครื่องมือแพทย์", value: 650000 },
    { name: "เครื่อง Ultherapy", code: "UT-003", category: "เครื่องมือแพทย์", value: 3200000 },
    { name: "เตียงทรีตเมนต์ไฟฟ้า #1", code: "BD-004", category: "เฟอร์นิเจอร์", value: 85000 },
  ],
  org_ridenow: [
    { name: "Honda Click 160i (ขาว)", code: "กท-1234", category: "มอเตอร์ไซค์", value: 62000 },
    { name: "Honda PCX 160 (ดำ)", code: "กท-5678", category: "มอเตอร์ไซค์", value: 89000 },
    { name: "Yamaha NMAX 155 (เทา)", code: "ขข-4321", category: "มอเตอร์ไซค์", value: 92000 },
    { name: "Honda Wave 110i (แดง)", code: "งง-8899", category: "มอเตอร์ไซค์", value: 44000 },
    { name: "Toyota Yaris ATIV (บรอนซ์)", code: "1กก-9911", category: "รถยนต์", value: 559000 },
    { name: "Honda Click 160i (ฟ้า)", code: "กท-7788", category: "มอเตอร์ไซค์", value: 62000 },
  ],
  org_bloom: [
    { name: "เครื่องพิมพ์ใบปะหน้า", code: "PR-001", category: "อุปกรณ์สำนักงาน", value: 8900 },
    { name: "ไฟสตูดิโอไลฟ์", code: "LT-002", category: "อุปกรณ์ไลฟ์", value: 15900 },
    { name: "เครื่องซีลถุง", code: "SL-003", category: "อุปกรณ์แพ็ก", value: 3900 },
  ],
};

const LEAD_STAGES = ["new", "contacted", "qualified", "negotiating", "won", "lost"];
const LEAD_SOURCES = ["Facebook Ads", "LINE OA", "TikTok", "เพื่อนแนะนำ", "Walk-in", "Google", "งานอีเวนต์"];

// --------------------------------------------------------------------------
// generator ต่อองค์กร
// --------------------------------------------------------------------------
const buildOrgData = (org) => {
  const branches = BRANCHES[org.id];
  const branchIds = branches.map((b) => b.id);
  const model = BUSINESS_MODEL_MAP[org.businessModel];
  const catalogSeed = CATALOG[org.id];

  // ----- staff -----
  const staff = STAFF_ROLES[org.id].flatMap((role, roleIndex) =>
    Array.from({ length: role === "ผู้จัดการสาขา" ? branchIds.length : 2 }).map((_, i) => ({
      id: `stf_${org.id}_${roleIndex}_${i}`,
      name: personName(),
      nickname: pick(NICK),
      position: role,
      branchId: role === "ผู้จัดการสาขา" ? branchIds[i] : pick(branchIds),
      phone: phone(),
      email: `staff${roleIndex}${i}@${org.id.replace("org_", "")}.co.th`,
      roleKey: role === "ผู้จัดการสาขา" ? "manager" : roleIndex === 0 ? "specialist" : "staff",
      commissionRate: role === "ที่ปรึกษาความงาม" || role === "แอดมินเพจ" ? 5 : role === "แพทย์" ? 10 : 0,
      isActive: chance(92),
      avatar: `${int(1, 30)}.png`,
      createdAt: shiftDays(-int(120, 900)),
    }))
  );

  // ----- catalog -----
  const products = catalogSeed.map((item, index) => ({
    id: `prd_${org.id}_${index}`,
    code: `${org.id.slice(4, 7).toUpperCase()}-${String(index + 1).padStart(3, "0")}`,
    ...item,
    category: item.type === "product" ? "สินค้า" : item.type === "package" ? "แพ็กเกจ" : item.type === "rental" ? "รายการเช่า" : "บริการ",
    isActive: chance(94),
    trackStock: item.type === "product",
    image: `${int(1, 30)}.png`,
    createdAt: shiftDays(-int(60, 700)),
  }));

  // ----- customers -----
  const customers = Array.from({ length: 42 }).map((_, index) => {
    const name = personName();
    const createdAt = shiftDays(-int(5, 730));
    return {
      id: `cus_${org.id}_${index}`,
      code: `C${String(index + 1001)}`,
      name,
      nickname: pick(NICK),
      phone: phone(),
      email: emailOf(index),
      lineId: chance(70) ? `@${name.split(" ")[0]}${int(10, 99)}` : "",
      birthDate: `19${int(70, 99)}-${String(int(1, 12)).padStart(2, "0")}-${String(int(1, 28)).padStart(2, "0")}`,
      gender: pick(["female", "male", "other"]),
      address: `${int(1, 400)}/${int(1, 99)} ถ.${pick(["สุขุมวิท", "พหลโยธิน", "นิมมานเหมินท์", "เจริญประดิษฐ์", "รัชดาภิเษก"])}`,
      province: pick(["กรุงเทพมหานคร", "เชียงใหม่", "เชียงราย", "ชลบุรี", "ขอนแก่น"]),
      branchId: pick(branchIds),
      ownerStaffId: pick(staff).id,
      channel: pick(CHANNELS),
      tier: pick(TIERS),
      points: int(0, 4800),
      totalSpend: money(0, 180000, 100),
      visits: int(0, 24),
      tags: [pick(["ลูกค้าประจำ", "สนใจโปรโมชั่น", "แพ้ยา", "ต้องติดตาม", "VIP", "ค้างชำระ"])],
      note: chance(35) ? "ลูกค้าขอให้โทรก่อนนัดทุกครั้ง" : "",
      isActive: chance(96),
      // ลูกค้านิติบุคคล 3 รายแรกของแต่ละร้าน (ไว้ลองออกใบกำกับภาษี) — เลขผู้เสียภาษีผ่าน checksum
      ...(index < 3
        ? {
            taxId: ["0107556001015", "0107556002020", "0107556003034"][index],
            branchCode: "00000",
            taxName: `บริษัท ${name.split(" ")[0]} จำกัด`,
            taxAddress: `${int(1, 400)} ถ.สีลม แขวงสุริยวงศ์ เขตบางรัก กรุงเทพฯ 10500`,
            isJuristic: true,
          }
        : {}),
      avatar: `${int(1, 30)}.png`,
      lastVisitAt: chance(80) ? shiftDays(-int(1, 120)) : null,
      createdAt,
    };
  });

  // ----- leads -----
  const leads = Array.from({ length: 22 }).map((_, index) => {
    const stage = pick(LEAD_STAGES);
    return {
      id: `led_${org.id}_${index}`,
      name: personName(),
      phone: phone(),
      channel: pick(CHANNELS),
      source: pick(LEAD_SOURCES),
      interestedIn: pick(products).name,
      value: money(2000, 90000, 500),
      stage,
      ownerStaffId: pick(staff).id,
      branchId: pick(branchIds),
      note: chance(50) ? "ขอให้ติดต่อกลับช่วงเย็น" : "",
      nextFollowUpAt: stage === "won" || stage === "lost" ? null : shiftDays(int(0, 10), int(9, 18)),
      createdAt: shiftDays(-int(1, 90)),
    };
  });

  // ----- resources -----
  const resources = RESOURCES[org.id].map((name, index) => ({
    id: `res_${org.id}_${index}`,
    name,
    type: model.resourceLabel,
    branchId: branchIds[index % branchIds.length],
    capacity: int(1, 4),
    status: pick(["available", "busy", "maintenance", "available", "available"]),
    isActive: true,
  }));

  // ----- assets -----
  const assets = ASSETS[org.id].map((item, index) => ({
    id: `ast_${org.id}_${index}`,
    ...item,
    branchId: pick(branchIds),
    status: pick(["available", "busy", "maintenance", "available"]),
    purchasedAt: shiftDays(-int(200, 1500)),
    lastServiceAt: shiftDays(-int(10, 200)),
    nextServiceAt: shiftDays(int(5, 120)),
    odometer: org.id === "org_ridenow" ? int(4000, 48000) : null,
    maintenanceLogs: Array.from({ length: int(1, 3) }).map((__, i) => ({
      id: `mnt_${index}_${i}`,
      date: shiftDays(-int(20, 400)),
      detail: pick(["เปลี่ยนถ่ายน้ำมันเครื่อง", "ตรวจเช็กประจำปี", "เปลี่ยนอะไหล่สึกหรอ", "ทำความสะอาดเครื่อง"]),
      cost: money(500, 12000, 100),
      vendor: pick(["ศูนย์บริการ", "ช่างภายใน", "ผู้จำหน่าย"]),
    })),
  }));

  // ----- inventory -----
  const inventory = products
    .filter((p) => p.trackStock)
    .map((p, index) => ({
      id: `inv_${org.id}_${index}`,
      productId: p.id,
      productName: p.name,
      branchId: pick(branchIds),
      quantity: int(0, 120),
      reorderPoint: int(10, 30),
      unit: p.unit,
      cost: p.cost,
      lastCountedAt: shiftDays(-int(1, 45)),
    }));

  const stockMoves = Array.from({ length: 26 }).map((_, index) => {
    const item = pick(inventory.length ? inventory : [{ productId: products[0].id, productName: products[0].name, unit: products[0].unit }]);
    const type = pick(["in", "out", "adjust"]);
    return {
      id: `stm_${org.id}_${index}`,
      productId: item.productId,
      productName: item.productName,
      branchId: pick(branchIds),
      type,
      quantity: type === "out" ? -int(1, 12) : int(1, 40),
      reason: type === "in" ? "รับเข้าจากผู้ขาย" : type === "out" ? "ตัดจ่ายจากการขาย" : "ปรับปรุงยอดจากการนับสต็อก",
      staffId: pick(staff).id,
      createdAt: shiftDays(-int(0, 60)),
    };
  });

  // ----- appointments -----
  const appointments = Array.from({ length: 68 }).map((_, index) => {
    const customer = pick(customers);
    const product = pick(products);
    const dayOffset = int(-30, 21);
    const start = shiftDays(dayOffset, int(9, 18));
    const status = dayOffset < 0 ? pick(["completed", "completed", "completed", "noshow", "cancelled"]) : pick(["pending", "confirmed", "confirmed"]);
    return {
      id: `apt_${org.id}_${index}`,
      code: `AP${String(index + 1).padStart(4, "0")}`,
      customerId: customer.id,
      customerName: customer.name,
      customerPhone: customer.phone,
      productId: product.id,
      serviceName: product.name,
      staffId: pick(staff).id,
      resourceId: pick(resources).id,
      branchId: customer.branchId,
      startAt: start,
      durationMin: product.duration || 60,
      status,
      depositAmount: chance(40) ? money(300, 3000, 100) : 0,
      depositPaid: chance(60),
      price: product.price,
      note: chance(25) ? "ลูกค้าขอเลื่อนได้ถ้าฝนตก" : "",
      createdAt: shiftDays(dayOffset - int(1, 14)),
    };
  });

  // ----- membership -----
  const tiers = [
    { id: `tir_${org.id}_1`, key: "bronze", name: "Bronze", minSpend: 0, pointRate: 1, discountPercent: 0, color: "#a97142", benefits: ["สะสมแต้ม 1 บาท = 1 คะแนน"] },
    { id: `tir_${org.id}_2`, key: "silver", name: "Silver", minSpend: 20000, pointRate: 1.2, discountPercent: 3, color: "#94a3b8", benefits: ["ส่วนลด 3%", "แต้มคูณ 1.2 เท่า"] },
    { id: `tir_${org.id}_3`, key: "gold", name: "Gold", minSpend: 60000, pointRate: 1.5, discountPercent: 5, color: "#eab308", benefits: ["ส่วนลด 5%", "จองคิวก่อนใคร", "ของขวัญวันเกิด"] },
    { id: `tir_${org.id}_4`, key: "platinum", name: "Platinum", minSpend: 150000, pointRate: 2, discountPercent: 10, color: "#6366f1", benefits: ["ส่วนลด 10%", "ที่ปรึกษาส่วนตัว", "ที่จอดรถฟรี"] },
  ];

  const rewards = [
    { id: `rwd_${org.id}_1`, name: "คูปองส่วนลด 200 บาท", points: 500, stock: 120, image: `${int(1, 30)}.png` },
    { id: `rwd_${org.id}_2`, name: "ทรีตเมนต์ / บริการฟรี 1 ครั้ง", points: 1500, stock: 40, image: `${int(1, 30)}.png` },
    { id: `rwd_${org.id}_3`, name: "ของที่ระลึกประจำร้าน", points: 300, stock: 200, image: `${int(1, 30)}.png` },
    { id: `rwd_${org.id}_4`, name: "อัปเกรดระดับสมาชิก 3 เดือน", points: 2500, stock: 10, image: `${int(1, 30)}.png` },
  ];

  const pointTransactions = Array.from({ length: 30 }).map((_, index) => {
    const customer = pick(customers);
    const earn = chance(70);
    return {
      id: `pts_${org.id}_${index}`,
      customerId: customer.id,
      customerName: customer.name,
      type: earn ? "earn" : "redeem",
      points: earn ? int(20, 900) : -int(100, 1500),
      detail: earn ? "สะสมจากการใช้บริการ" : `แลก ${pick(rewards).name}`,
      createdAt: shiftDays(-int(0, 180)),
    };
  });

  // ----- courses / packages ที่ลูกค้าถืออยู่ -----
  const customerPackages = Array.from({ length: 18 }).map((_, index) => {
    const customer = pick(customers);
    const product = pick(products.filter((p) => p.sessions).length ? products.filter((p) => p.sessions) : products);
    const total = product.sessions || int(4, 10);
    const used = int(0, total);
    return {
      id: `pkg_${org.id}_${index}`,
      customerId: customer.id,
      customerName: customer.name,
      productId: product.id,
      packageName: product.name,
      totalSessions: total,
      usedSessions: used,
      price: product.price,
      expireAt: shiftDays(int(20, 400)),
      branchId: customer.branchId,
      createdAt: shiftDays(-int(10, 300)),
    };
  });

  // ----- chat -----
  const chatThreads = Array.from({ length: 12 }).map((_, index) => {
    const customer = pick(customers);
    const channel = pick(["line", "facebook", "instagram"]);
    return {
      id: `cht_${org.id}_${index}`,
      customerId: customer.id,
      customerName: customer.name,
      avatar: customer.avatar,
      channel,
      lastMessage: pick(["สนใจโปรโมชั่นนี้ค่ะ", "ขอราคาหน่อยครับ", "โอนแล้วนะคะ", "พรุ่งนี้ว่างไหมครับ", "ขอที่อยู่ร้านหน่อยค่ะ", "ของถึงยังคะ"]),
      unread: chance(45) ? int(1, 6) : 0,
      assignedStaffId: pick(staff).id,
      status: pick(["open", "open", "pending", "closed"]),
      updatedAt: shiftDays(-int(0, 6), int(8, 22)),
    };
  });

  const chatMessages = chatThreads.flatMap((thread) =>
    Array.from({ length: int(4, 9) }).map((_, i) => ({
      id: `msg_${thread.id}_${i}`,
      threadId: thread.id,
      from: i % 2 === 0 ? "customer" : "staff",
      text: i % 2 === 0
        ? pick(["สนใจโปรโมชั่นนี้ค่ะ", "ราคาเท่าไหร่คะ", "มีคิวว่างวันไหนบ้างครับ", "โอนแล้วค่ะ ส่งสลิปให้นะคะ", "ขอบคุณค่ะ"])
        : pick(["สวัสดีค่ะ ยินดีให้บริการค่ะ", "รอสักครู่นะคะ กำลังตรวจสอบให้", "ราคานี้รวมทุกอย่างแล้วค่ะ", "ได้รับสลิปเรียบร้อยค่ะ", "ขอบคุณที่ใช้บริการค่ะ"]),
      createdAt: shiftDays(-int(0, 5), int(8, 21)),
    }))
  );

  // ----- slips -----
  const slips = Array.from({ length: 16 }).map((_, index) => {
    const customer = pick(customers);
    const status = pick(["verified", "verified", "pending", "rejected"]);
    return {
      id: `slp_${org.id}_${index}`,
      customerId: customer.id,
      customerName: customer.name,
      amount: money(200, 25000, 50),
      bank: pick(["SCB", "KBANK", "BBL", "KTB", "TTB", "BAY"]),
      transferAt: shiftDays(-int(0, 20), int(8, 22)),
      status,
      matchedInvoiceNo: status === "verified" ? `INV-${int(1000, 1999)}` : null,
      reason: status === "rejected" ? pick(["ยอดเงินไม่ตรง", "สลิปซ้ำกับรายการก่อนหน้า", "อ่านสลิปไม่ออก"]) : "",
      channel: pick(["line", "app", "walkin"]),
      createdAt: shiftDays(-int(0, 20)),
    };
  });

  // ----- campaigns -----
  const campaigns = Array.from({ length: 8 }).map((_, index) => {
    const status = pick(["draft", "scheduled", "sent", "sent"]);
    const audience = int(120, 3200);
    const sent = status === "sent" ? audience : 0;
    return {
      id: `cmp_${org.id}_${index}`,
      name: pick(["โปรโมชั่นวันเกิด", "ลูกค้าเก่ากลับมาใช้บริการ", "แจ้งเตือนค่างวด", "เปิดตัวบริการใหม่", "ลดราคาสิ้นเดือน", "เชิญร่วมงานอีเวนต์"]),
      channel: pick(["line", "sms", "email"]),
      segment: pick(["ลูกค้าทั้งหมด", "สมาชิก Gold ขึ้นไป", "ลูกค้าที่ไม่มาเกิน 90 วัน", "ลูกหนี้ค้างชำระ", "ลูกค้าใหม่เดือนนี้"]),
      status,
      audienceCount: audience,
      sentCount: sent,
      openRate: status === "sent" ? int(28, 72) : 0,
      clickRate: status === "sent" ? int(4, 26) : 0,
      scheduledAt: status === "scheduled" ? shiftDays(int(1, 14), int(9, 18)) : null,
      sentAt: status === "sent" ? shiftDays(-int(1, 40)) : null,
      message: "สวัสดีค่ะ คุณ {{ชื่อ}} ทางร้านมีโปรโมชั่นพิเศษสำหรับคุณ กดดูรายละเอียดได้เลยค่ะ",
      createdAt: shiftDays(-int(2, 60)),
    };
  });

  // ----- OMS orders (ออเดอร์จากแชท/ไลฟ์) -----
  const salesOrders = Array.from({ length: 30 }).map((_, index) => {
    const customer = pick(customers);
    const items = Array.from({ length: int(1, 3) }).map(() => {
      const product = pick(products);
      const qty = int(1, 3);
      return { productId: product.id, name: product.name, price: product.price, quantity: qty, total: product.price * qty };
    });
    const total = items.reduce((sum, item) => sum + item.total, 0);
    return {
      id: `ord_${org.id}_${index}`,
      orderNo: `SO-${String(index + 2001)}`,
      customerId: customer.id,
      customerName: customer.name,
      channel: pick(["line", "facebook", "instagram", "live", "walkin"]),
      items,
      subtotal: total,
      shippingFee: chance(60) ? 60 : 0,
      discount: chance(30) ? money(20, 300, 10) : 0,
      total,
      paymentStatus: pick(["paid", "paid", "unpaid", "pending"]),
      shippingStatus: pick(["packing", "shipped", "delivered", "pending", "cancelled"]),
      trackingNo: chance(60) ? `TH${int(10000000, 99999999)}` : "",
      branchId: customer.branchId,
      staffId: pick(staff).id,
      createdAt: shiftDays(-int(0, 45)),
    };
  });

  // ----- POS sales -----
  const posSales = Array.from({ length: 24 }).map((_, index) => {
    const items = Array.from({ length: int(1, 4) }).map(() => {
      const product = pick(products);
      const qty = int(1, 2);
      return { productId: product.id, name: product.name, price: product.price, quantity: qty, total: product.price * qty };
    });
    const total = items.reduce((sum, item) => sum + item.total, 0);
    return {
      id: `pos_${org.id}_${index}`,
      billNo: `POS-${String(index + 5001)}`,
      branchId: pick(branchIds),
      staffId: pick(staff).id,
      customerId: chance(60) ? pick(customers).id : null,
      items,
      total,
      paymentMethod: pick(["cash", "qr", "card", "qr"]),
      createdAt: shiftDays(-int(0, 30), int(9, 20)),
    };
  });

  // ----- quotations / invoices -----
  const quotations = Array.from({ length: 12 }).map((_, index) => {
    const customer = pick(customers);
    const items = Array.from({ length: int(1, 4) }).map(() => {
      const product = pick(products);
      const qty = int(1, 3);
      return { name: product.name, price: product.price, quantity: qty, total: product.price * qty };
    });
    const subtotal = items.reduce((sum, item) => sum + item.total, 0);
    const discount = Math.min(chance(35) ? money(200, 3000, 100) : 0, Math.floor(subtotal / 2));
    return {
      id: `qtn_${org.id}_${index}`,
      docNo: `QT-${String(index + 3001)}`,
      customerId: customer.id,
      customerName: customer.name,
      items,
      subtotal,
      discount,
      vatPercent: 7,
      total: Math.round((subtotal - discount) * 1.07),
      status: pick(["draft", "sent", "accepted", "expired", "sent"]),
      validUntil: shiftDays(int(3, 30)),
      staffId: pick(staff).id,
      branchId: customer.branchId,
      createdAt: shiftDays(-int(1, 60)),
    };
  });

  const invoices = Array.from({ length: 32 }).map((_, index) => {
    const customer = pick(customers);
    const items = Array.from({ length: int(1, 3) }).map(() => {
      const product = pick(products);
      const qty = int(1, 2);
      return { name: product.name, price: product.price, quantity: qty, total: product.price * qty };
    });
    const subtotal = items.reduce((sum, item) => sum + item.total, 0);
    // ส่วนลดไม่เกินครึ่งของยอดรวม กันยอดสุทธิ <= 0
    const discount = Math.min(chance(25) ? money(100, 2000, 100) : 0, Math.floor(subtotal / 2));
    const total = Math.round((subtotal - discount) * 1.07);
    const dueAt = shiftDays(int(-25, 25));
    const status = new Date(dueAt) < now() ? pick(["paid", "paid", "overdue"]) : pick(["unpaid", "paid", "unpaid"]);
    return {
      id: `inv_${org.id}_${index}`,
      docNo: `INV-${String(index + 1001)}`,
      customerId: customer.id,
      customerName: customer.name,
      customerPhone: customer.phone,
      items,
      subtotal,
      discount,
      vatPercent: 7,
      total,
      paidAmount: status === "paid" ? total : 0,
      status,
      dueAt,
      paidAt: status === "paid" ? shiftDays(-int(0, 25)) : null,
      paymentMethod: status === "paid" ? pick(["qr", "cash", "transfer", "card"]) : null,
      branchId: customer.branchId,
      staffId: pick(staff).id,
      createdAt: shiftDays(-int(2, 70)),
    };
  });

  const receipts = invoices
    .filter((inv) => inv.status === "paid")
    .slice(0, 14)
    .map((inv, index) => ({
      id: `rcp_${org.id}_${index}`,
      docNo: `RC-${String(index + 4001)}`,
      invoiceId: inv.id,
      invoiceNo: inv.docNo,
      customerId: inv.customerId,
      customerName: inv.customerName,
      total: inv.total,
      paidAt: inv.paidAt,
      paymentMethod: inv.paymentMethod,
      createdAt: inv.paidAt,
    }));

  // ----- payment links -----
  const paymentLinks = Array.from({ length: 10 }).map((_, index) => {
    const customer = pick(customers);
    const status = pick(["active", "paid", "expired", "active"]);
    return {
      id: `pyl_${org.id}_${index}`,
      code: `PL${int(100000, 999999)}`,
      title: pick(["ค่ามัดจำบริการ", "ชำระค่างวด", "ค่าสินค้าตามออเดอร์", "ค่าบริการเพิ่มเติม"]),
      customerId: customer.id,
      customerName: customer.name,
      amount: money(500, 45000, 100),
      status,
      transactionId: status === "paid" ? `TXN-${int(100000, 999999)}` : null,
      expireAt: shiftDays(int(1, 14)),
      createdAt: shiftDays(-int(0, 30)),
    };
  });

  // ลิงก์ชำระของใบแจ้งหนี้ที่ยังไม่จ่าย 2 ใบแรก (ใช้ลองหน้า /pay/:code) — รหัสคงที่ต่อร้านเพื่อให้ทดสอบง่าย
  invoices
    .filter((inv) => inv.status !== "paid")
    .slice(0, 2)
    .forEach((inv, index) => {
      paymentLinks.push({
        id: `pyl_${org.id}_inv${index}`,
        code: `PLDEMO${org.id.slice(4, 6).toUpperCase()}${index + 1}`,
        title: `ชำระ ${inv.docNo}`,
        invoiceId: inv.id,
        docNo: inv.docNo,
        customerId: inv.customerId,
        customerName: inv.customerName,
        amount: inv.total,
        status: "active",
        expireAt: shiftDays(7),
        createdAt: shiftDays(-1),
      });
    });
  // ลิงก์ที่จ่ายแล้วบางส่วนโอนให้ร้านแล้ว (ไว้ดูส่วน "ยอดรอโอน")
  paymentLinks.forEach((link, index) => {
    if (link.status === "paid") {
      link.paidAt = link.createdAt;
      link.payoutStatus = index % 2 === 0 ? "transferred" : "pending";
    }
  });

  // ----- เอกสารภาษี (เฉพาะร้านที่จด VAT) -----
  const taxYear = now().getFullYear();
  const taxDocuments = [];
  const docSequences = [];
  if (org.vatRegistered) {
    const seller = {
      name: org.name, nameEn: org.nameEn, taxId: org.taxId, branchCode: org.branchCode || "00000", branchName: "",
      address: org.address, phone: org.phone, vatRegistered: true,
    };
    const buyerOf = (customer) => ({
      name: customer.taxName || customer.name, taxId: customer.taxId || "", branchCode: customer.branchCode || "00000",
      address: customer.taxAddress || customer.address, isJuristic: !!customer.isJuristic,
    });
    const paid = invoices.filter((inv) => inv.status === "paid").slice(0, 3);
    paid.forEach((inv, index) => {
      const customer = customers.find((c) => c.id === inv.customerId);
      const calc = calcVat({ lines: inv.items, discount: inv.discount, mode: "exclusive" });
      const issued = index < 2;
      taxDocuments.push({
        id: `txd_${org.id}_${index}`,
        docNo: issued ? `RT-${taxYear}-${String(index + 1).padStart(6, "0")}` : "",
        docType: "receipt_tax_invoice",
        status: issued ? "issued" : "draft",
        invoiceId: inv.id,
        invoiceNo: inv.docNo,
        customerId: inv.customerId,
        seller,
        buyer: buyerOf(customer),
        items: inv.items.map((item) => ({ name: item.name, price: item.price, quantity: item.quantity, total: item.total })),
        priceMode: "exclusive",
        vatRate: 7,
        subtotal: calc.subtotal,
        discount: calc.discount,
        vatBase: calc.base,
        vatAmount: calc.vat,
        total: calc.total,
        sourceTotal: inv.total,
        rdStatus: issued ? "not_sent" : undefined,
        issuedAt: issued ? inv.paidAt : null,
        createdAt: inv.paidAt || shiftDays(-3),
      });
    });
    docSequences.push({ id: `receipt_tax_invoice:${taxYear}`, docType: "receipt_tax_invoice", year: taxYear, last: 2, createdAt: shiftDays(-5) });
  }

  // ----- contracts -----
  const contracts = Array.from({ length: 12 }).map((_, index) => {
    const customer = pick(customers);
    const status = pick(["draft", "waiting_sign", "active", "active", "expired", "terminated"]);
    const startAt = shiftDays(-int(0, 300));
    return {
      id: `ctr_${org.id}_${index}`,
      docNo: `CT-${String(index + 7001)}`,
      title: pick(["สัญญาเช่ารถจักรยานยนต์", "สัญญาซื้อขายแพ็กเกจบริการ", "สัญญาเช่าห้องพัก", "สัญญาสินเชื่อส่วนบุคคล", "สัญญาให้บริการรายปี"]),
      customerId: customer.id,
      customerName: customer.name,
      value: money(5000, 350000, 500),
      status,
      startAt,
      endAt: iso(new Date(new Date(startAt).getTime() + int(30, 720) * DAY)),
      signedAt: status === "active" || status === "expired" ? shiftDays(-int(1, 250)) : null,
      signedBy: status === "active" || status === "expired" ? customer.name : null,
      assetId: (ASSETS[org.id] && chance(50)) ? `ast_${org.id}_${int(0, ASSETS[org.id].length - 1)}` : null,
      branchId: customer.branchId,
      terms: [
        "ผู้เช่า/ผู้ซื้อ ตกลงชำระค่าบริการตามกำหนดที่ระบุในสัญญา",
        "กรณีผิดนัดชำระเกิน 7 วัน ผู้ให้บริการมีสิทธิ์ระงับการให้บริการ",
        "ความเสียหายที่เกิดจากการใช้งานผิดวิธี ผู้เช่าเป็นผู้รับผิดชอบ",
      ],
      createdAt: startAt,
    };
  });

  // ----- installments -----
  const installmentPlans = Array.from({ length: 10 }).map((_, index) => {
    const customer = pick(customers);
    const principal = money(15000, 240000, 1000);
    const periods = pick([3, 6, 9, 12, 24]);
    const interestRate = pick([0, 0.5, 0.99, 1.25]);
    const perPeriod = Math.round((principal * (1 + (interestRate / 100) * periods)) / periods);
    const startAt = shiftDays(-int(30, 300));
    const schedule = Array.from({ length: periods }).map((_, i) => {
      const dueAt = iso(new Date(new Date(startAt).getTime() + (i + 1) * 30 * DAY));
      const paid = new Date(dueAt) < now() ? chance(85) : false;
      return {
        period: i + 1,
        dueAt,
        amount: perPeriod,
        status: paid ? "paid" : new Date(dueAt) < now() ? "overdue" : "unpaid",
        paidAt: paid ? dueAt : null,
      };
    });
    return {
      id: `ist_${org.id}_${index}`,
      contractNo: `LN-${String(index + 8001)}`,
      customerId: customer.id,
      customerName: customer.name,
      customerPhone: customer.phone,
      principal,
      periods,
      interestRate,
      perPeriod,
      startAt,
      schedule,
      outstanding: schedule.filter((s) => s.status !== "paid").reduce((sum, s) => sum + s.amount, 0),
      branchId: customer.branchId,
      createdAt: startAt,
    };
  });

  // ----- commissions -----
  const commissions = Array.from({ length: 26 }).map((_, index) => {
    const member = pick(staff);
    const base = money(3000, 90000, 500);
    const rate = member.commissionRate || pick([3, 5, 8, 10]);
    return {
      id: `com_${org.id}_${index}`,
      staffId: member.id,
      staffName: member.name,
      position: member.position,
      refDoc: `INV-${int(1001, 1032)}`,
      baseAmount: base,
      rate,
      amount: Math.round((base * rate) / 100),
      period: `${new Date().getFullYear()}-${String(int(1, 12)).padStart(2, "0")}`,
      status: pick(["pending", "approved", "paid", "approved"]),
      branchId: member.branchId,
      createdAt: shiftDays(-int(1, 120)),
    };
  });

  // ----- field service jobs -----
  const fieldJobs = Array.from({ length: 14 }).map((_, index) => {
    const customer = pick(customers);
    return {
      id: `fjb_${org.id}_${index}`,
      jobNo: `JB-${String(index + 9001)}`,
      customerId: customer.id,
      customerName: customer.name,
      address: customer.address,
      type: pick(["ติดตั้ง", "ซ่อมบำรุง", "ตรวจเช็กประจำปี", "ส่งมอบสินค้า", "รับคืนสินค้า"]),
      assignedStaffId: pick(staff).id,
      status: pick(["assigned", "in_progress", "done", "pending", "done"]),
      scheduledAt: shiftDays(int(-10, 12), int(8, 17)),
      note: chance(40) ? "โทรหาลูกค้าก่อนเข้า 30 นาที" : "",
      branchId: customer.branchId,
      createdAt: shiftDays(-int(0, 30)),
    };
  });

  // ----- documents & notifications & activities -----
  const documents = Array.from({ length: 16 }).map((_, index) => {
    const customer = pick(customers);
    return {
      id: `doc_${org.id}_${index}`,
      customerId: customer.id,
      customerName: customer.name,
      name: pick(["สำเนาบัตรประชาชน", "ใบขับขี่", "ใบรับรองแพทย์", "สัญญาเช่าฉบับลงนาม", "ใบเสร็จรับเงิน", "ผลตรวจก่อนทำหัตถการ"]),
      type: pick(["id_card", "driver_license", "medical", "contract", "receipt", "other"]),
      size: `${int(120, 4800)} KB`,
      uploadedBy: chance(50) ? "customer" : "staff",
      createdAt: shiftDays(-int(1, 200)),
    };
  });

  const notifications = Array.from({ length: 14 }).map((_, index) => ({
    id: `ntf_${org.id}_${index}`,
    title: pick(["นัดหมายใหม่", "ชำระเงินสำเร็จ", "สลิปรอตรวจสอบ", "ค่างวดใกล้ครบกำหนด", "สต็อกใกล้หมด", "สัญญาใกล้หมดอายุ"]),
    detail: pick([
      "มีลูกค้าจองคิวเข้ามาใหม่ กรุณาตรวจสอบตารางนัด",
      "ได้รับชำระเงินผ่าน QR เรียบร้อยแล้ว",
      "มีสลิปโอนเงินรอการตรวจสอบ 1 รายการ",
      "ลูกค้ามีค่างวดครบกำหนดใน 3 วัน",
      "สินค้าคงเหลือต่ำกว่าจุดสั่งซื้อซ้ำ",
      "สัญญาจะหมดอายุภายใน 15 วัน",
    ]),
    audience: pick(["admin", "customer"]),
    read: chance(45),
    createdAt: shiftDays(-int(0, 14), int(8, 20)),
  }));

  const activities = Array.from({ length: 40 }).map((_, index) => {
    const customer = pick(customers);
    return {
      id: `act_${org.id}_${index}`,
      customerId: customer.id,
      customerName: customer.name,
      type: pick(["appointment", "payment", "order", "chat", "point", "contract"]),
      title: pick(["เข้ารับบริการ", "ชำระเงิน", "สั่งซื้อสินค้า", "ติดต่อผ่านแชท", "ได้รับแต้มสะสม", "ลงนามสัญญา"]),
      detail: pick([
        "ใช้บริการที่สาขาหลัก",
        "ชำระผ่าน QR PromptPay",
        "สั่งซื้อผ่านช่องทางออนไลน์",
        "สอบถามโปรโมชั่นผ่าน LINE",
        "รับแต้มจากยอดใช้จ่าย",
        "ลงนามสัญญาเรียบร้อย",
      ]),
      amount: chance(60) ? money(200, 25000, 50) : null,
      staffId: pick(staff).id,
      createdAt: shiftDays(-int(0, 200), int(8, 20)),
    };
  });

  const wallets = customers.slice(0, 20).map((customer, index) => ({
    id: `wlt_${org.id}_${index}`,
    customerId: customer.id,
    balance: money(0, 8000, 50),
    points: customer.points,
    coupons: Array.from({ length: int(0, 3) }).map((_, i) => ({
      id: `cpn_${org.id}_${index}_${i}`,
      name: pick(["ส่วนลด 100 บาท", "ส่งฟรี", "ลด 10% สูงสุด 500", "ทรีตเมนต์ฟรี 1 ครั้ง"]),
      expireAt: shiftDays(int(5, 120)),
    })),
  }));

  const faqs = [
    { id: `faq_${org.id}_1`, question: "จองคิวล่วงหน้าได้กี่วัน?", answer: "จองล่วงหน้าได้สูงสุด 60 วัน ผ่านหน้าเว็บหรือแจ้งเจ้าหน้าที่ได้เลย", category: "การจอง" },
    { id: `faq_${org.id}_2`, question: "ชำระเงินได้ช่องทางไหนบ้าง?", answer: "รองรับ QR PromptPay, เงินสดหน้าร้าน และโอนเงินพร้อมแจ้งสลิปในระบบ", category: "การชำระเงิน" },
    { id: `faq_${org.id}_3`, question: "ยกเลิกหรือเลื่อนนัดได้ไหม?", answer: "แจ้งล่วงหน้าอย่างน้อย 24 ชั่วโมง สามารถเลื่อนได้โดยไม่เสียค่าใช้จ่าย", category: "การจอง" },
    { id: `faq_${org.id}_4`, question: "แต้มสะสมหมดอายุเมื่อไหร่?", answer: "แต้มมีอายุ 12 เดือนนับจากวันที่ได้รับ ตรวจสอบได้ที่หน้ากระเป๋าเงิน", category: "สมาชิก" },
    { id: `faq_${org.id}_5`, question: "ขอใบกำกับภาษีได้อย่างไร?", answer: "แจ้งเจ้าหน้าที่ตอนชำระเงิน หรือขอย้อนหลังได้ภายใน 7 วันผ่านแชท", category: "เอกสาร" },
  ];

  return {
    branches,
    staff,
    products,
    customers,
    leads,
    resources,
    assets,
    inventory,
    stockMoves,
    appointments,
    tiers,
    rewards,
    pointTransactions,
    customerPackages,
    chatThreads,
    chatMessages,
    slips,
    campaigns,
    salesOrders,
    posSales,
    quotations,
    invoices,
    receipts,
    paymentLinks,
    taxDocuments,
    docSequences,
    contracts,
    installmentPlans,
    commissions,
    fieldJobs,
    documents,
    notifications,
    activities,
    wallets,
    faqs,
  };
};

// --------------------------------------------------------------------------
// รวมทุกองค์กร + ข้อมูล global
// --------------------------------------------------------------------------
export const buildSeed = () => {
  seedState = 987654321;

  const seed = {
    global: {
      organizations: ORGS.map((org) => ({
        ...org,
        // แพ็กเกจของร้านตัวอย่าง — ยังไม่หมดอายุ (org_bloom เหลือ 5 วัน ไว้ดูการเตือนใกล้หมดอายุ)
        planStatus: "active",
        planStartAt: shiftDays(-30 * (org.planMonths || 1)),
        planExpireAt: org.id === "org_bloom" ? shiftDays(5) : shiftDays(30 * (org.planMonths || 1)),
        planLastPaidAt: shiftDays(-30 * (org.planMonths || 1)),
        // โมดูลที่เปิดต้องไม่เกินสิทธิ์ของแพ็กเกจ
        enabledModules: BUSINESS_MODEL_MAP[org.businessModel].modules
          .filter((key) => planModules(org.plan).includes(key))
          .concat(org.id === "org_glow" ? ["etax", "online_payment"] : []),
        // ร้านตัวอย่างที่ซื้อฟีเจอร์เสริม e-Tax + รับชำระออนไลน์ (หมดอายุพร้อมแพ็กเกจหลัก)
        ...(org.id === "org_glow"
          ? {
              addons: ["etax", "online_payment"].map((key) => ({
                id: `adn_${key}`, kind: "module", key, name: key, expireAt: shiftDays(30 * (org.planMonths || 1)), purchaseId: `demo_${key}`,
              })),
            }
          : {}),
        createdAt: shiftDays(-int(200, 900)),
      })),
      subscriptions: ORGS.map((org) => ({
        id: `sub_${org.id}`,
        org_id: org.id,
        orgName: org.name,
        plan: org.plan,
        planName: getPlan(org.plan)?.name || org.plan,
        cycle: org.planCycle,
        months: org.planMonths,
        amount: priceOf(org.plan, org.planCycle),
        status: "paid",
        transactionId: `demo_${org.id}`,
        refNo: `${Date.now()}`.slice(0, 10),
        startAt: shiftDays(-30 * (org.planMonths || 1)),
        expireAt: org.id === "org_bloom" ? shiftDays(5) : shiftDays(30 * (org.planMonths || 1)),
        createdAt: shiftDays(-30 * (org.planMonths || 1)),
      })),
      roles: [
        { id: "role_owner", name: "เจ้าของธุรกิจ", nameEn: "Owner", permissions: ["*"], isSystem: true },
        { id: "role_manager", name: "ผู้จัดการสาขา", nameEn: "Branch Manager", permissions: ["dashboard.view", "customer.*", "booking.*", "billing.*", "inventory.*", "staff.view"], isSystem: false },
        { id: "role_specialist", name: "ผู้ให้บริการ", nameEn: "Specialist", permissions: ["dashboard.view", "booking.view", "booking.update", "customer.view"], isSystem: false },
        { id: "role_staff", name: "พนักงานทั่วไป", nameEn: "Staff", permissions: ["dashboard.view", "customer.view", "booking.view", "pos.use"], isSystem: false },
        { id: "role_finance", name: "การเงิน", nameEn: "Finance", permissions: ["dashboard.view", "billing.*", "installment.*", "commission.*"], isSystem: false },
      ],
      permissionGroups: [
        { key: "dashboard", name: "แดชบอร์ด", actions: ["view"] },
        { key: "customer", name: "ลูกค้า", actions: ["view", "create", "update", "delete"] },
        { key: "booking", name: "การจอง", actions: ["view", "create", "update", "delete"] },
        { key: "billing", name: "การเงิน", actions: ["view", "create", "update", "delete"] },
        { key: "inventory", name: "สต็อก", actions: ["view", "create", "update"] },
        { key: "staff", name: "พนักงาน", actions: ["view", "create", "update", "delete"] },
        { key: "pos", name: "ขายหน้าร้าน", actions: ["use", "refund"] },
        { key: "setting", name: "ตั้งค่าระบบ", actions: ["view", "update"] },
      ],
      notificationSettings: {
        lineToken: "",
        lineEnabled: true,
        events: [
          { key: "booking_created", name: "มีการจองใหม่", enabled: true },
          { key: "payment_received", name: "ได้รับชำระเงิน", enabled: true },
          { key: "slip_pending", name: "มีสลิปรอตรวจสอบ", enabled: true },
          { key: "installment_due", name: "ค่างวดใกล้ครบกำหนด", enabled: true },
          { key: "stock_low", name: "สต็อกต่ำกว่ากำหนด", enabled: false },
          { key: "contract_expiring", name: "สัญญาใกล้หมดอายุ", enabled: false },
        ],
      },
    },
  };

  ORGS.forEach((org) => {
    seed[org.id] = buildOrgData(org);
  });

  return seed;
};

export { ORGS, dateOnly };
